import { Inject, Injectable, OnDestroy } from '@angular/core';
import * as R from 'ramda';
import { BehaviorSubject, delay, Subscription } from 'rxjs';

import { StorageService, StorageServiceToken } from '../services';
import { concatUniq, hasNoValue,hasValue} from '../util';
import { FeatureGuideElement } from './feature-guide-element';
import { COMPLETED_FEATURE_GUIDES_STORAGE_KEY } from './util';

const DEFAULT_ACTIVE_INDEX = -1;

@Injectable({
  providedIn: 'root'
})
export class FeatureGuideService implements OnDestroy {

  private featureGuideIds: string[] = [];
  private completedFeatureGuideIds: string[] = [];
  private incompleteFeatureGuideIds: string[] = [];

  private activeIndex = DEFAULT_ACTIVE_INDEX;
  private active = new BehaviorSubject<FeatureGuideElement[]>([]);

  private registered = new BehaviorSubject<FeatureGuideElement[]>([]);
  private registered$ = this.registered.asObservable();
  private registeredSubscription = new Subscription();
  private registeredFeatureGuides: FeatureGuideElement[][] = [];
  private registeredFeatureGuidesIds: string[] = [];

  get registeredValue() {
    return this.registered.getValue() || [];
  }

  get activeValue() {
    return this.active.getValue() || [];
  }

  constructor(@Inject(StorageServiceToken) private readonly storageService: StorageService) {
    this.setCompletedFeatureGuides();
    this.setIncompleteFeatureGuides();
    this.registeredSubscription = this.registered$.pipe(
      delay(100)
    ).subscribe(featureGuides => {
      const groupedFeatureGuides = this.groupFeatureGuides(
        this.sortFeatureGuides(
          this.cleanUpCompletedFeatureGuides(featureGuides)
        )
      );

      this.registeredFeatureGuides = Object.values(groupedFeatureGuides);
      this.registeredFeatureGuidesIds = Object.keys(groupedFeatureGuides);

      if (this.hasActiveNotRegistered()) {
        this.cleanUpActive();
        this.setActiveIndex();
      } else if (this.hasWrongActive()) {
        this.cleanUpActive();
        this.activate();
      } else if (this.startWithActivation()) {
        const activeIndex = this.calculateActiveIndex();
        this.setActiveIndex(activeIndex);
        this.activate();
      } else if (this.hasNewFeatureGuidesReadyForActivation()) {
        this.activate();
      }
    });
  }

  ngOnDestroy(): void {
    this.registeredSubscription.unsubscribe();
  }

  initialize(featureGuideIds: string[], completedFeatureGuideIds: string[]) {
    this.featureGuideIds = featureGuideIds;
    this.activeIndex = DEFAULT_ACTIVE_INDEX;
    this.setCompletedFeatureGuides(completedFeatureGuideIds);
    this.setIncompleteFeatureGuides();
  }

  register(featureGuide: FeatureGuideElement) {
    if (!this.isCompleted(featureGuide.getId())) {
      this.registered.next([...this.registeredValue, featureGuide]);
    }
  }

  unregister(featureGuide: FeatureGuideElement) {
    this.registeredFeatureGuidesIds = R.filter(id => featureGuide.getId() !== id, this.registeredFeatureGuidesIds);
    this.registeredFeatureGuides = R.map(featureGuides =>
      R.filter(t => t.getId() !== featureGuide.getId(), featureGuides),
    this.registeredFeatureGuides
    );
    this.cleanUpRegistered();
    this.cleanUpActive();
  }

  hasPrevious(): boolean {
    return this.hasRegisteredFeatureGuides() && this.activeIndex - 1 >= 0;
  }

  hasNext(): boolean {
    return this.hasRegisteredFeatureGuides() && this.activeIndex < this.registeredFeatureGuides.length - 1;
  }

  previous() {
    if (this.hasPrevious()) {
      this.deactivate();
      this.activeIndex--;
      this.activate();
    }
  }

  next() {
    if (this.hasNext()) {
      this.deactivate();
      this.activeIndex++;
      this.activate();
    }
  }

  complete() {
    if (this.hasRegisteredFeatureGuides()) {
      this.deactivate();
      this.activeIndex = DEFAULT_ACTIVE_INDEX;
      this.completedFeatureGuideIds.push(...this.registeredFeatureGuidesIds);
      this.registeredFeatureGuides = [];
      this.registeredFeatureGuidesIds = [];
      this.setIncompleteFeatureGuides();
      this.cleanUpRegistered();
      this.cleanUpActive();
      this.writeCompletedFeatureGuides();
    }
  }

  getRegisteredFeatureGuidesIds(): string[] {
    return this.registeredFeatureGuidesIds;
  }

  private setCompletedFeatureGuides(completedFeatureGuides: string[] = []) {
    const completedFeatureGuideIdsFromStorage = this.storageService.read(COMPLETED_FEATURE_GUIDES_STORAGE_KEY) as string[];
    this.completedFeatureGuideIds = hasValue(completedFeatureGuideIdsFromStorage)
      ? concatUniq(completedFeatureGuideIdsFromStorage, completedFeatureGuides) as string[]
      : completedFeatureGuides;
  }

  private setIncompleteFeatureGuides() {
    this.incompleteFeatureGuideIds = R.filter(featureGuide => !R.includes(featureGuide, this.completedFeatureGuideIds),
      this.featureGuideIds);
  }

  private setActiveIndex(activeIndex = DEFAULT_ACTIVE_INDEX) {
    this.activeIndex = activeIndex;
  }

  private calculateActiveIndex() {
    const incompleteFeatureGuide = this.findRegisteredIncompleteFeatureGuideId();
    return R.findIndex(R.equals(incompleteFeatureGuide), this.registeredFeatureGuidesIds);
  }

  private writeCompletedFeatureGuides() {
    this.storageService.write(COMPLETED_FEATURE_GUIDES_STORAGE_KEY, this.completedFeatureGuideIds);
  }

  private activate() {
    const featureGuides = this.registeredFeatureGuides[this.activeIndex];
    if (hasValue(featureGuides)) {
      featureGuides.forEach(featureGuide => featureGuide.show());
      this.active.next(featureGuides);
    }
  }

  private deactivate() {
    const featureGuides = this.registeredFeatureGuides[this.activeIndex];
    if (hasValue(featureGuides)) {
      featureGuides.forEach(featureGuide => featureGuide.hide());
      this.active.next([]);
    }
  }

  private cleanUpCompletedFeatureGuides(featureGuides: FeatureGuideElement[]): FeatureGuideElement[] {
    return hasValue(this?.completedFeatureGuideIds)
      ? R.filter(featureGuide => R.not(R.includes(featureGuide.getId(), this.completedFeatureGuideIds)), featureGuides)
      : featureGuides;
  }

  private sortFeatureGuides(featureGuides: FeatureGuideElement[]): FeatureGuideElement[] {
    return hasValue(this?.featureGuideIds)
      ? R.sortBy(R.pipe(R.prop('id'), R.indexOf(R.__, this.featureGuideIds)))(featureGuides) as FeatureGuideElement[]
      : featureGuides;
  }

  private groupFeatureGuides(featureGuides: FeatureGuideElement[]) {
    return R.groupBy(featureGuide => featureGuide.getId(), featureGuides);
  }

  private isCompleted(id: string) {
    return this.completedFeatureGuideIds && R.includes(id, this.completedFeatureGuideIds);
  }

  private hasRegisteredFeatureGuides() {
    return hasValue(this.registeredFeatureGuidesIds);
  }

  private startWithActivation() {
    return hasNoValue(this.activeValue) && hasValue(this.findRegisteredIncompleteFeatureGuideId());
  }

  private hasNewFeatureGuidesReadyForActivation() {
    if (this.activeIndex === DEFAULT_ACTIVE_INDEX) {
      return false;
    }

    return hasValue(this.registeredFeatureGuidesIds)
      && this.registeredFeatureGuidesIds[this.activeIndex].length !== this.activeValue.length;
  }

  private hasActiveNotRegistered() {
    return hasNoValue(this.registeredFeatureGuidesIds) && hasValue(this.activeValue);
  }

  private hasWrongActive() {
    return hasValue(this.activeValue) && !R.equals(this.registeredFeatureGuides[this.activeIndex], this.activeValue);
  }

  private findRegisteredIncompleteFeatureGuideId() {
    return R.find(registeredFeatureGuide => R.includes(registeredFeatureGuide, this.incompleteFeatureGuideIds),
      this.registeredFeatureGuidesIds);
  }

  private cleanUpRegistered() {
    const newRegistered = R.filter(featureGuide => R.includes(featureGuide.getId(),
      this.registeredFeatureGuidesIds), this.registeredValue);
    this.registered.next(newRegistered);
  }

  private cleanUpActive() {
    this.activeValue.forEach(guide => guide.hide());
    this.active.next([]);
  }
}
